import { useMemo } from 'react';
import { Check, CircleCheck, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toReadableDiffLine } from '../utils-display';
import type {
	PersonaChangeComment,
	PersonaChangeCommentKind,
	PersonaChanges,
	PersonaDiffRow,
	PersonaDiffRowType
} from '@/types';

interface PersonaChangesPanelProps {
	changes: PersonaChanges;
	// The feedback box's current text: a suggestion already in it shows as added and cannot be added again.
	feedback: string;
	onUseSuggestion: (suggestion: string) => void;
}

// Unchanged lines kept on each side of a change; longer unchanged stretches collapse to a count.
const CONTEXT_LINES = 2;

const KIND_LABEL: Record<PersonaChangeCommentKind, string> = {
	conflict: 'Conflict',
	unclear: 'Unclear',
	structure: 'Structure'
};

const ROW_STYLE: Record<PersonaDiffRowType, { label: string; className: string }> = {
	added: { label: 'Added', className: 'bg-green-50 text-green-900' },
	removed: { label: 'Removed', className: 'bg-red-50 text-red-800 line-through decoration-red-300' },
	unchanged: { label: '', className: 'text-muted-foreground' }
};

type DiffItem =
	| { kind: 'row'; row: PersonaDiffRow; comments: PersonaChangeComment[] }
	| { kind: 'gap'; hiddenLines: number };

/** Changed rows with a little context, collapsed gaps between them, and each comment under the added row it quotes. */
function buildDiffItems(
	rows: PersonaDiffRow[],
	comments: PersonaChangeComment[]
): { items: DiffItem[]; unpinned: PersonaChangeComment[] } {
	const commentsByRow = new Map<number, PersonaChangeComment[]>();
	const unpinned: PersonaChangeComment[] = [];
	for (const comment of comments) {
		const index = rows.findIndex((row) => row.type === 'added' && row.text === comment.line);
		if (index === -1) unpinned.push(comment);
		else commentsByRow.set(index, [...(commentsByRow.get(index) ?? []), comment]);
	}

	const isNearChange = (index: number): boolean =>
		rows
			.slice(Math.max(0, index - CONTEXT_LINES), index + CONTEXT_LINES + 1)
			.some((row) => row.type !== 'unchanged');

	const items: DiffItem[] = [];
	let hiddenLines = 0;
	rows.forEach((row, index) => {
		if (!isNearChange(index)) {
			hiddenLines += 1;
			return;
		}
		if (hiddenLines > 0) items.push({ kind: 'gap', hiddenLines });
		hiddenLines = 0;
		items.push({ kind: 'row', row, comments: commentsByRow.get(index) ?? [] });
	});
	if (hiddenLines > 0 && items.length > 0) items.push({ kind: 'gap', hiddenLines });

	return { items, unpinned };
}

/** One guide line shown as readable text rather than markdown; a table's `| --- |` rule renders nothing. */
function DiffLine({ row }: { row: PersonaDiffRow }) {
	const readable = toReadableDiffLine(row.text);
	if (!readable) return null;
	const style = ROW_STYLE[row.type];
	return (
		<div
			className={cn(
				'flex gap-1.5 whitespace-pre-wrap break-words px-3 py-0.5',
				style.className,
				readable.kind === 'heading' && 'font-semibold'
			)}
		>
			{style.label && <span className='sr-only'>{style.label}: </span>}
			{readable.kind === 'bullet' && <span aria-hidden>•</span>}
			<span>{readable.text || ' '}</span>
		</div>
	);
}

function ReviewStatus({ changes, hasChanges }: { changes: PersonaChanges; hasChanges: boolean }) {
	const { review } = changes;
	if (!hasChanges) {
		return (
			<p className='text-xs text-muted-foreground'>
				Your feedback didn't change the guide. Try more specific feedback.
			</p>
		);
	}
	if (!review) return null;
	if (review.status === 'failed') {
		return (
			<p className='text-xs text-muted-foreground'>We couldn't check these changes for conflicting or unclear rules.</p>
		);
	}
	if (review.comments.length === 0) {
		return (
			<p className='flex items-center gap-1 text-xs text-green-700'>
				<CircleCheck className='size-3.5' />
				No conflicting or unclear rules in your changes.
			</p>
		);
	}
	return (
		<p className='flex items-center gap-1 text-xs text-yellow-700'>
			<TriangleAlert className='size-3.5' />
			{review.comments.length} {review.comments.length === 1 ? 'change' : 'changes'} could confuse the model. Use
			the suggestions below and regenerate.
		</p>
	);
}

/** The suggestion as feedback, naming the line it is about so the regenerate edits that line. */
function suggestionFeedback(comment: PersonaChangeComment): string {
	const line = toReadableDiffLine(comment.line)?.text || comment.quote;
	return `About "${line}": ${comment.suggestion}`;
}

function CommentCard({
	comment,
	isAdded,
	onUseSuggestion
}: {
	comment: PersonaChangeComment;
	isAdded: boolean;
	onUseSuggestion: (suggestion: string) => void;
}) {
	return (
		<div className='mx-3 my-2 flex flex-col gap-1.5 rounded-xl border border-yellow-200 bg-yellow-50 p-3 font-sans'>
			<div className='flex items-center gap-2'>
				<Badge variant='warning'>{KIND_LABEL[comment.kind]}</Badge>
				{comment.section && <span className='text-[11px] text-muted-foreground'>{comment.section}</span>}
			</div>
			<p className='text-xs text-gray-900'>{comment.comment}</p>
			{comment.conflictsWith && (
				<p className='text-[11px] text-muted-foreground'>Conflicts with: "{comment.conflictsWith}"</p>
			)}
			<div className='flex items-start justify-between gap-3'>
				<p className='text-xs text-gray-900'>
					<span className='font-medium'>Suggestion:</span> {comment.suggestion}
				</p>
				<Button
					variant='outline'
					size='sm'
					className='shrink-0 font-normal'
					disabled={isAdded}
					onClick={() => onUseSuggestion(suggestionFeedback(comment))}
				>
					{isAdded ? (
						<>
							<Check className='size-3.5' />
							Added
						</>
					) : (
						'Use as feedback'
					)}
				</Button>
			</div>
		</div>
	);
}

/** What the last regenerate changed in the guide, with review comments pinned to the lines they are about. */
export function PersonaChangesPanel({ changes, feedback, onUseSuggestion }: PersonaChangesPanelProps) {
	const { items, unpinned } = useMemo(
		() => buildDiffItems(changes.rows, changes.review?.comments ?? []),
		[changes]
	);
	const hasChanges = changes.rows.some((row) => row.type !== 'unchanged');
	const renderComment = (comment: PersonaChangeComment, key: string) => (
		<CommentCard
			key={key}
			comment={comment}
			isAdded={feedback.includes(comment.suggestion)}
			onUseSuggestion={onUseSuggestion}
		/>
	);

	return (
		<section aria-label='What changed' className='flex flex-col gap-3 rounded-2xl border bg-white p-4'>
			<div className='flex flex-col gap-1'>
				<h3 className='text-sm font-medium'>What changed</h3>
				{changes.feedbackText && (
					<p className='text-xs text-muted-foreground'>Your feedback: "{changes.feedbackText}"</p>
				)}
				<ReviewStatus changes={changes} hasChanges={hasChanges} />
			</div>

			{unpinned.map((comment, index) => renderComment(comment, `unpinned-${index}`))}

			{hasChanges && (
				<div className='rounded-xl border py-1 text-xs'>
					{items.map((item, index) =>
						item.kind === 'gap' ? (
							<div key={`gap-${index}`} className='bg-gray-50 px-3 py-1 font-sans text-[11px] text-muted-foreground'>
								{item.hiddenLines} unchanged line{item.hiddenLines === 1 ? '' : 's'}
							</div>
						) : (
							<div key={`row-${index}`}>
								<DiffLine row={item.row} />
								{item.comments.map((comment, commentIndex) => renderComment(comment, `comment-${commentIndex}`))}
							</div>
						)
					)}
				</div>
			)}
		</section>
	);
}
